import firebase from 'firebase/compat/app';
import { User, UserData } from 'hydra-data-types';
import { getUserDocRef } from '../helpers/CommonDatabaseOperations';
import { Analytics } from './Analytics';
import { Auth } from './Auth';
import { isUs65Plus } from './MedicareAccess';

type EmailSignInProps = {
  email: string;
  password: string;
  userId: string;
};

export async function emailSignIn(
  email: string,
  password: string
): Promise<User['userId'] | undefined> {
  const firebaseAuth = firebase.auth();
  const userCredential = await firebaseAuth.signInWithEmailAndPassword(email, password);
  return userCredential?.user?.uid;
}

export async function tokenSignIn(authToken: string): Promise<User['userId'] | undefined> {
  const firebaseAuth = firebase.auth();
  const userCredential = await firebaseAuth.signInWithCustomToken(authToken);
  return userCredential?.user?.uid;
}

export async function emailSignInAndAccountValidation({
  email,
  password,
  userId,
}: EmailSignInProps): Promise<void> {
  try {
    const authUserId = await emailSignIn(email, password);
    if (userId !== authUserId) {
      throw Error(
        `Recently created userId ${userId} does not match signed in userId ${authUserId}`
      );
    }
  } catch (error) {
    // If a wrong password error occurs, it means a user is trying to register with an email that already exists
    // and an error to show the email already exists should be shown instead of a wrong password error
    if (Auth.isAuthError(error) && (error.code as unknown as string) === 'auth/wrong-password') {
      const error: firebase.auth.Error = {
        code: 'auth/email-already-exists',
        message: 'An account with this email already exists.',
        name: 'AuthError',
      };
      throw error;
    }
    throw error;
  }
}

/** Call `Analytics.identify`, and `Analytics.track` with the correct traits structure. */
export function identifyAndTrack(
  event: typeof Analytics.Event.a_login_succeeded | typeof Analytics.Event.a_signup_succeeded,
  user: User,
  userData: UserData
): void {
  const { userId, email, name } = user;
  const lastName = 'lastName' in user ? user.lastName : '';
  const { age, gender } = userData;

  const identifyTraits = { userId, email, name, lastName, age, gender };
  Analytics.identify(identifyTraits, event);

  // Amplitude expects age to be a number, not an array here
  const trackingTraits = { userId, email, name, lastName, age: age?.[0], gender };
  if (event === Analytics.Event.a_signup_succeeded) {
    void trackSignupSucceeded(user, userData);
  } else {
    Analytics.track({ event, ...trackingTraits });
  }

  // sending h_hcp_referred event now as Vero needs it to be sent AFTER identify has been sent
  if (Array.isArray(userData.doctor) && userData.doctor[0] === 'Yes') {
    Analytics.track({ event: Analytics.Event.h_hcp_referred });
  }
}

async function trackSignupSucceeded(user: User, userData: UserData): Promise<void> {
  // The store is dynamically imported to avoid a circular import: AuthUtils is part of the store's
  // init graph (Store → UserActions → Auth → AuthUtils), so a static import would cycle.
  const { store } = await import('../store/Store');
  const state = store.getState();
  Analytics.track({
    event: Analytics.Event.a_signup_succeeded,
    userId: user.userId,
    email: user.email,
    name: user.name,
    lastName: 'lastName' in user ? user.lastName : '',
    age: userData.age?.[0],
    gender: userData.gender,
    isUs65Plus: isUs65Plus(state),
    country: state.entry.country,
  });
}

export async function fetchUser(userId: User['userId']): Promise<User> {
  const userDoc = await getUserDocRef(userId).get();
  return userDoc.data() as User;
}
